import React, { useState } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Loader2, Sparkles, CheckCircle2, Trash2, ChefHat } from "lucide-react";

const CAT_LABELS = {
  starter: "Entrant",
  main: "Principal",
  dessert: "Postres",
  drink: "Beguda",
  side: "Guarnició",
  sauce: "Salsa",
  bread: "Pa",
  other: "Altres",
};

const CAT_COLORS = {
  starter: "bg-amber-100 text-amber-700",
  main: "bg-red-100 text-red-700",
  dessert: "bg-pink-100 text-pink-700",
  drink: "bg-blue-100 text-blue-700",
  side: "bg-green-100 text-green-700",
  sauce: "bg-orange-100 text-orange-700",
  bread: "bg-yellow-100 text-yellow-700",
  other: "bg-gray-100 text-gray-700",
};

export default function MenuImportModal({ open, onClose, venueId, onImported }) {
  const [step, setStep] = useState("input"); // "input" | "review" | "done"
  const [menuText, setMenuText] = useState("");
  const [inputMode, setInputMode] = useState("text"); // "text" | "pdf" | "url" | "photo"
  const [pdfFile, setPdfFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [webUrl, setWebUrl] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState(null);
  const [dishes, setDishes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState({});

  const handleExtract = async () => {
    if (inputMode === "text" && !menuText.trim()) return;
    if (inputMode === "pdf" && !pdfFile) return;
    if (inputMode === "url" && !webUrl.trim()) return;
    if (inputMode === "photo" && !photoFile) return;
    setExtracting(true);
    setExtractError(null);
    try {
      let fileUrl = null;
      if (inputMode === "pdf") {
        const uploaded = await hosys.integrations.Core.UploadFile({ file: pdfFile });
        fileUrl = uploaded.file_url;
      } else if (inputMode === "photo") {
        const uploaded = await hosys.integrations.Core.UploadFile({ file: photoFile });
        fileUrl = uploaded.file_url;
      }

      const prompt = `Analitza la carta de restaurant i extreu tots els plats.
Per a cada plat, proporciona la informació en el format JSON indicat.
Classifica la categoria amb un d'aquests valors: starter, main, dessert, drink, side, sauce, bread, other.
Si el text inclou preu, extreu-lo. Si inclou descripció o ingredients, extreu-los.
${inputMode === "text" ? `\nText de la carta:\n---\n${menuText}\n---` : ""}
${inputMode === "url" ? `\nAnalitza la carta d'aquest web: ${webUrl}` : ""}
${inputMode === "pdf" ? "\nAnalitza el PDF de la carta adjunt i extreu tots els plats que hi apareguin." : ""}
${inputMode === "photo" ? "\nAnalitza la imatge/foto de la carta adjunta." : ""}
Retorna un array JSON amb tots els plats detectats.`;

      const result = await hosys.integrations.Core.InvokeLLM({
        prompt,
        add_context_from_internet: inputMode === "url",
        file_urls: fileUrl ? [fileUrl] : undefined,
        response_json_schema: {
          type: "object",
          properties: {
            dishes: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  name: { type: "string" },
                  category: { type: "string" },
                  description: { type: "string" },
                  selling_price: { type: "number" },
                  allergens: { type: "array", items: { type: "string" } },
                },
              },
            },
          },
        },
      });

      const extracted = result?.dishes || [];
      if (extracted.length === 0) {
        setExtractError("No s'han detectat plats. Comprova que el fitxer conté una carta llegible i torna-ho a intentar.");
        return;
      }
      setDishes(extracted);
      const sel = {};
      extracted.forEach((_, i) => { sel[i] = true; });
      setSelected(sel);
      setStep("review");
    } catch (err) {
      setExtractError("S'ha produït un error analitzant la carta: " + (err?.message || "Error desconegut"));
    } finally {
      setExtracting(false);
    }
  };

  const handleImport = async () => {
    setSaving(true);
    const toImport = dishes.filter((_, i) => selected[i]);
    try {
      const batchSize = 50;
      for (let i = 0; i < toImport.length; i += batchSize) {
        const batch = toImport.slice(i, i + batchSize);
        await hosys.entities.RecipeSheet.bulkCreate(
          batch.map(d => ({
            venue_id: venueId,
            name: d.name,
            category: d.category || "other",
            description: d.description || "",
            selling_price: d.selling_price || undefined,
            allergens: d.allergens || [],
            ingredients: [],
          }))
        );
      }
      setStep("done");
      onImported();
    } catch (err) {
      setExtractError("Error important plats: " + (err?.message || "Error desconegut"));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setStep("input");
    setMenuText("");
    setPdfFile(null);
    setPhotoFile(null);
    setWebUrl("");
    setInputMode("text");
    setDishes([]);
    setSelected({});
    onClose();
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#7B2D3F]" />
            Importar carta massivament
          </DialogTitle>
        </DialogHeader>

        {step === "input" && (
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Puja un PDF o enganxa el text de la carta i la IA extraurà tots els plats automàticament.
            </p>

            {/* Mode tabs */}
            <div className="grid grid-cols-2 gap-1.5 border rounded-xl p-1 bg-gray-50">
              {[
                { key: "text", emoji: "📋", label: "Text" },
                { key: "pdf", emoji: "📄", label: "PDF" },
                { key: "url", emoji: "🌐", label: "Enllaç web" },
                { key: "photo", emoji: "📷", label: "Foto" },
              ].map(({ key, emoji, label }) => (
                <button
                  key={key}
                  onClick={() => setInputMode(key)}
                  className={`py-2 text-sm font-medium rounded-lg transition-all ${inputMode === key ? "bg-white shadow text-gray-900" : "text-gray-500"}`}
                >
                  {emoji} {label}
                </button>
              ))}
            </div>

            {inputMode === "text" && (
              <Textarea
                placeholder={`Entrants\n- Amanida mixta ..... 7,50€\n- Croquetes de pernil (8u) ...... 9,00€\n\nPrincipals\n- Bacallà a la llauna ..... 16,00€\n- Entrecot de vedella ..... 22,00€\n\nPostres\n- Crema catalana ..... 5,00€`}
                value={menuText}
                onChange={(e) => setMenuText(e.target.value)}
                className="h-48 resize-none font-mono text-sm"
              />
            )}

            {inputMode === "url" && (
              <div className="space-y-2">
                <p className="text-xs text-gray-500">Enganxa l'URL de la pàgina web amb la carta del restaurant.</p>
                <Input
                  placeholder="https://www.elrestaurant.com/carta"
                  value={webUrl}
                  onChange={(e) => setWebUrl(e.target.value)}
                  type="url"
                />
              </div>
            )}

            {(inputMode === "pdf" || inputMode === "photo") && (() => {
              const isPdf = inputMode === "pdf";
              const file = isPdf ? pdfFile : photoFile;
              const setFile = isPdf ? setPdfFile : setPhotoFile;
              const accept = isPdf ? ".pdf" : "image/*";
              const id = isPdf ? "pdf-upload" : "photo-upload";
              const emoji = isPdf ? "📄" : "📷";
              const label = isPdf ? "Fes clic per seleccionar un PDF" : "Fes clic per seleccionar una foto";
              const hint = isPdf ? "Carta en format PDF (màx. 10 MB)" : "JPG, PNG, WEBP (màx. 10 MB)";
              return (
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center">
                  <input type="file" accept={accept} id={id} className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                  {file ? (
                    <div className="space-y-2">
                      <p className="text-2xl">{emoji}</p>
                      <p className="font-medium text-gray-900">{file.name}</p>
                      <p className="text-xs text-gray-400">{(file.size / 1024).toFixed(0)} KB</p>
                      <button onClick={() => setFile(null)} className="text-xs text-red-500 underline">Eliminar</button>
                    </div>
                  ) : (
                    <label htmlFor={id} className="cursor-pointer space-y-2 block">
                      <p className="text-3xl">{emoji}</p>
                      <p className="text-sm font-medium text-gray-700">{label}</p>
                      <p className="text-xs text-gray-400">{hint}</p>
                    </label>
                  )}
                </div>
              );
            })()}

            {extractError && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">
                ⚠️ {extractError}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>Cancel·lar</Button>
              <Button
                onClick={handleExtract}
                disabled={
                  (inputMode === "text" && !menuText.trim()) ||
                  (inputMode === "pdf" && !pdfFile) ||
                  (inputMode === "url" && !webUrl.trim()) ||
                  (inputMode === "photo" && !photoFile) ||
                  extracting
                }
                className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2"
              >
                {extracting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Analitzant carta...</>
                ) : (
                  <><Sparkles className="w-4 h-4" /> Extreure plats</>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "review" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                S'han detectat <strong>{dishes.length} plats</strong>. Selecciona els que vols importar.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => { const s = {}; dishes.forEach((_, i) => { s[i] = true; }); setSelected(s); }}
                  className="text-xs text-[#7B2D3F] underline"
                >
                  Tots
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={() => setSelected({})}
                  className="text-xs text-gray-500 underline"
                >
                  Cap
                </button>
              </div>
            </div>

            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
              {dishes.map((dish, i) => (
                <div
                  key={`dish-${i}`}
                  onClick={() => setSelected(prev => ({ ...prev, [i]: !prev[i] }))}
                  className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                    selected[i]
                      ? "border-[#7B2D3F] bg-[#7B2D3F]/5"
                      : "border-gray-200 bg-white opacity-50"
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    selected[i] ? "border-[#7B2D3F] bg-[#7B2D3F]" : "border-gray-300"
                  }`}>
                    {selected[i] && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 text-sm">{dish.name}</p>
                      <Badge variant="secondary" className={`text-[10px] border-0 ${CAT_COLORS[dish.category] || CAT_COLORS.other}`}>
                        {CAT_LABELS[dish.category] || "Altres"}
                      </Badge>
                    </div>
                    {dish.description && (
                      <p className="text-xs text-gray-500 truncate mt-0.5">{dish.description}</p>
                    )}
                    {dish.allergens?.length > 0 && (
                      <p className="text-xs text-red-500 mt-0.5">⚠ {dish.allergens.join(", ")}</p>
                    )}
                  </div>
                  {dish.selling_price && (
                    <span className="text-sm font-semibold text-gray-700 flex-shrink-0">
                      {dish.selling_price.toFixed(2)}€
                    </span>
                  )}
                </div>
              ))}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep("input")}>Tornar</Button>
              <Button
                onClick={handleImport}
                disabled={selectedCount === 0 || saving}
                className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Important...</>
                ) : (
                  <><ChefHat className="w-4 h-4" /> Importar {selectedCount} plats</>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "done" && (
          <div className="py-10 text-center space-y-3">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Importació completada</h3>
            <p className="text-sm text-gray-500">
              S'han creat <strong>{selectedCount} fitxes tècniques</strong> correctament.<br />
              Ara pots editar cada una per afegir ingredients, al·lèrgens i instruccions.
            </p>
            <Button onClick={handleClose} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] mt-2">
              Tancar
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}